"use client";

import React from "react";
import { Card, CardContent, Spinner } from "@heroui/react";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";

import { logger } from "@/telemetry";
import { normalizeText, extractSalientTokens } from "@/utils/textMatching";

export interface ExcelViewerProps {
  /** Excel document as ArrayBuffer */
  documentData: ArrayBuffer;

  /** Citation text used to highlight matching rows in the table */
  citationText?: string;

  /** Filename for display purposes */
  filename?: string;
}

interface SheetData {
  name: string;
  headers: string[];
  rows: any[][];
}

/** Rows shown per sheet. One more is read so the header row does not count against it. */
const MAX_DISPLAYED_ROWS = 1000;

/** Character code of "A", the letter a blank header's column is named from. */
const FIRST_COLUMN_LETTER_CODE = 65;

/**
 * Find rows that match the citation text using token overlap scoring.
 *
 * For each row, all cell values are concatenated and normalised. Salient tokens
 * from the citation are compared against the row string. Rows with at least 30%
 * token overlap are considered matches.
 *
 * @param sheet - The current sheet data (headers + rows)
 * @param citationText - Citation text to match against
 * @returns Set of row indices that match
 */
function findMatchingRows(sheet: SheetData, citationText: string): Set<number> {
  const matchingIndices = new Set<number>();

  if (!citationText || !sheet.rows.length) {
    return matchingIndices;
  }

  const normalisedCitation = normalizeText(citationText);
  const salientTokens = extractSalientTokens(normalisedCitation);

  if (salientTokens.length === 0) {
    return matchingIndices;
  }

  for (let rowIdx = 0; rowIdx < sheet.rows.length; rowIdx++) {
    const row = sheet.rows[rowIdx];
    // Concatenate all cell values into a single string for matching
    const rowText = row
      .map((cell: any) => (cell !== null && cell !== undefined ? String(cell) : ""))
      .join(" ");
    const normalisedRow = normalizeText(rowText);

    // Score = proportion of salient tokens found in the row
    const matchedCount = salientTokens.filter((token) => normalisedRow.includes(token)).length;
    const score = matchedCount / salientTokens.length;

    if (score >= 0.3) {
      matchingIndices.add(rowIdx);
    }
  }

  return matchingIndices;
}

/** The lowest index among the matching rows, or -1 when there are none. */
function findFirstMatchIndex(matchingRows: Set<number>): number {
  let firstMatchIdx = -1;

  if (matchingRows.size > 0) {
    for (const idx of matchingRows) {
      if (firstMatchIdx === -1 || idx < firstMatchIdx) firstMatchIdx = idx;
    }
  }

  return firstMatchIdx;
}

interface SheetTabsProps {
  sheets: SheetData[];
  activeSheet: string;
  onSelect: (name: string) => void;
  /** Prefixes the tab and panel ids, so two viewers on one page cannot collide. */
  idPrefix: string;
}

/** The id of a sheet's tab, which labels the panel while that sheet is shown. */
const sheetTabId = (idPrefix: string, index: number) => `${idPrefix}-tab-${index}`;

/** The id of the one panel every tab controls, since only the active sheet is rendered. */
const sheetPanelId = (idPrefix: string) => `${idPrefix}-panel`;

/** The keys that move between sheet tabs, each giving the tab to move to. */
const TAB_KEY_TARGETS: Record<string, (index: number, count: number) => number> = {
  ArrowRight: (index, count) => (index + 1) % count,
  ArrowLeft: (index, count) => (index - 1 + count) % count,
  Home: () => 0,
  End: (_index, count) => count - 1,
};

/**
 * One tab per sheet, shown when the workbook has more than one. Only the active tab is
 * in the tab order, and the arrow keys, Home and End move between tabs and select the
 * one they land on, as the ARIA tabs pattern expects. Every tab controls the panel
 * `SheetView` renders the table into. A key held with Alt, Ctrl or Meta is left to the
 * browser, so shortcuts such as Alt+Left still go back.
 */
const SheetTabs = ({ sheets, activeSheet, onSelect, idPrefix }: SheetTabsProps) => {
  const { t } = useTranslation();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    const target = TAB_KEY_TARGETS[event.key];

    if (!target || event.altKey || event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    const next = target(index, sheets.length);

    onSelect(sheets[next].name);
    const nextTab = event.currentTarget.parentElement?.children[next];

    if (nextTab instanceof HTMLElement) nextTab.focus();
  };

  return (
    <Card>
      <CardContent className="p-2">
        <div
          aria-label={t("citations.excelViewer.sheetsLabel")}
          className="flex border-b border-default-200"
          role="tablist"
        >
          {sheets.map((sheet, index) => {
            const isActive = activeSheet === sheet.name;

            return (
              <button
                key={sheet.name}
                aria-controls={sheetPanelId(idPrefix)}
                aria-selected={isActive}
                className={`px-4 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                  isActive
                    ? "border-b-2 border-accent text-accent"
                    : "text-default-500 hover:text-default-700"
                }`}
                id={sheetTabId(idPrefix, index)}
                role="tab"
                tabIndex={isActive ? 0 : -1}
                type="button"
                onClick={() => onSelect(sheet.name)}
                onKeyDown={(event) => handleKeyDown(event, index)}
              >
                {sheet.name}
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

interface SheetRowProps {
  row: SheetData["rows"][number];
  rowIdx: number;
  headers: string[];
  isMatch: boolean;
  /** Set on the first matching row only, so the viewer can scroll to it. */
  rowRef: React.RefObject<HTMLTableRowElement | null> | undefined;
}

const SheetRow = ({ row, rowIdx, headers, isMatch, rowRef }: SheetRowProps) => (
  <tr
    ref={rowRef}
    className={
      isMatch
        ? "border-b border-default-200 bg-warning-soft"
        : "border-b border-default-200 hover:bg-default-50"
    }
    data-highlight={isMatch ? "true" : undefined}
  >
    {/* Row number */}
    <td className="px-3 py-2 text-xs text-default-500 border-r border-default-200 bg-default-50">
      {rowIdx + 1}
    </td>
    {headers.map((_, cellIdx) => {
      const cellValue = row[cellIdx];

      return (
        <td
          key={cellIdx}
          className="px-3 py-2 text-xs text-default-700 border-r border-default-200 whitespace-nowrap"
        >
          {cellValue !== null && cellValue !== undefined ? String(cellValue) : ""}
        </td>
      );
    })}
  </tr>
);

interface SheetTableProps {
  sheet: SheetData;
  matchingRows: Set<number>;
  firstMatchRef: React.RefObject<HTMLTableRowElement | null>;
}

/** The sheet's rows under a sticky header, with matching rows highlighted. */
const SheetTable = ({ sheet, matchingRows, firstMatchRef }: SheetTableProps) => {
  const { t } = useTranslation();
  // Determine the first matching row index for the ref assignment
  const firstMatchIdx = findFirstMatchIndex(matchingRows);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-default-100 sticky top-0 z-10">
          <tr>
            {/* Row number column */}
            <th className="px-3 py-2 text-left text-xs font-semibold text-default-600 border-r border-default-200">
              #
            </th>
            {sheet.headers.map((header, idx) => (
              <th
                key={idx}
                className="px-3 py-2 text-left text-xs font-semibold text-default-600 border-r border-default-200 whitespace-nowrap"
              >
                {header ||
                  t("citations.excelViewer.column", {
                    letter: String.fromCharCode(FIRST_COLUMN_LETTER_CODE + idx),
                  })}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sheet.rows.map((row, rowIdx) => (
            <SheetRow
              key={rowIdx}
              headers={sheet.headers}
              isMatch={matchingRows.has(rowIdx)}
              row={row}
              rowIdx={rowIdx}
              rowRef={rowIdx === firstMatchIdx ? firstMatchRef : undefined}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface SheetViewProps {
  sheets: SheetData[];
  activeSheet: string;
  currentSheet: SheetData;
  onSelectSheet: (name: string) => void;
  matchingRows: Set<number>;
  firstMatchRef: React.RefObject<HTMLTableRowElement | null>;
}

/** The loaded workbook: sheet tabs or the sheet's name, its table, and a truncation note. */
const SheetView = ({
  sheets,
  activeSheet,
  currentSheet,
  onSelectSheet,
  matchingRows,
  firstMatchRef,
}: SheetViewProps) => {
  const { t } = useTranslation();
  const idPrefix = React.useId();
  const hasTabs = sheets.length > 1;
  // With tabs, the table sits in the panel they control, labelled by the active tab and
  // focusable, because a table holds nothing a keyboard user could otherwise tab to.
  const panelProps = hasTabs
    ? {
        "aria-labelledby": sheetTabId(
          idPrefix,
          sheets.findIndex((sheet) => sheet.name === activeSheet),
        ),
        className:
          "rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
        id: sheetPanelId(idPrefix),
        role: "tabpanel",
        tabIndex: 0,
      }
    : {};

  return (
    <div className="w-full h-[75vh] overflow-auto bg-content1 p-8">
      <div className="max-w-full space-y-4">
        {/* Sheet tabs (if multiple sheets) */}
        {hasTabs && (
          <SheetTabs
            activeSheet={activeSheet}
            idPrefix={idPrefix}
            sheets={sheets}
            onSelect={onSelectSheet}
          />
        )}

        {/* Single sheet name (if only one sheet) */}
        {sheets.length === 1 && (
          <div className="text-sm font-medium text-default-600 px-2">
            {t("citations.excelViewer.sheetName", { name: currentSheet.name })}
          </div>
        )}

        {/* Table Display */}
        <div {...panelProps}>
          <Card>
            <CardContent className="p-0">
              {currentSheet.rows.length === 0 ? (
                <div className="flex items-center justify-center h-32">
                  <p className="text-default-500">{t("No data in this sheet")}</p>
                </div>
              ) : (
                <SheetTable
                  firstMatchRef={firstMatchRef}
                  matchingRows={matchingRows}
                  sheet={currentSheet}
                />
              )}
            </CardContent>
          </Card>
        </div>

        {/* Show info if table is truncated */}
        {currentSheet.rows.length === MAX_DISPLAYED_ROWS && (
          <div className="text-xs text-default-500 text-center">
            {t("citations.excelViewer.rowsTruncated", { rows: MAX_DISPLAYED_ROWS })}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Excel Viewer component using sheetjs (xlsx)
 * Displays Excel files with multi-sheet support and citation row highlighting.
 *
 * When citation text is provided, rows with sufficient token overlap are
 * highlighted and the view auto-scrolls to the first match.
 */
export const ExcelViewer: React.FC<ExcelViewerProps> = ({ documentData, citationText }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [sheets, setSheets] = React.useState<SheetData[]>([]);
  const [activeSheet, setActiveSheet] = React.useState<string>("");

  // Parse Excel data
  React.useEffect(() => {
    if (!documentData) {
      return;
    }

    let isMounted = true;

    const parseExcel = async () => {
      try {
        setLoading(true);
        setError(null);

        // Read Excel file using xlsx
        const workbook = XLSX.read(documentData, { type: "array" });

        if (!isMounted) return;

        if (workbook.SheetNames.length === 0) {
          setError(t("No sheets found in Excel file"));
          setLoading(false);

          return;
        }

        // Parse all sheets
        const parsedSheets: SheetData[] = workbook.SheetNames.map((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];

          // Convert sheet to array of arrays
          const data: any[][] = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
            blankrows: false,
          });

          // Limit to first 1000 rows for performance
          const limitedData = data.slice(0, MAX_DISPLAYED_ROWS + 1);

          if (limitedData.length === 0) {
            return {
              name: sheetName,
              headers: [],
              rows: [],
            };
          }

          // First row as headers
          const headers = limitedData[0].map((h: any) => String(h || ""));

          // Remaining rows as data
          const rows = limitedData.slice(1);

          return {
            name: sheetName,
            headers,
            rows,
          };
        });

        setSheets(parsedSheets);
        setActiveSheet(parsedSheets[0]?.name || "");
        setLoading(false);
      } catch (err) {
        logger.error("❌ Failed to parse Excel", err);
        if (isMounted) {
          setError(t("Failed to parse Excel file"));
          setLoading(false);
        }
      }
    };

    parseExcel();

    return () => {
      isMounted = false;
    };
    // `t` is stable in production; including it causes test workers to
    // infinite-loop when the test mock returns a new `t` on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentData]);

  // Derive currentSheet early so hooks below can depend on it.
  // Falls back to an empty sheet when data has not loaded yet.
  const currentSheet = sheets.find((s) => s.name === activeSheet) || sheets[0];

  // Compute which rows match the citation text (token overlap >= 30%).
  // Returns an empty set when there is no sheet data or no citation.
  const matchingRows = React.useMemo(
    () => (currentSheet ? findMatchingRows(currentSheet, citationText || "") : new Set<number>()),
    [currentSheet, citationText],
  );

  // Ref for the first highlighted row so we can auto-scroll to it
  const firstMatchRef = React.useRef<HTMLTableRowElement | null>(null);

  // Auto-scroll to the first matching row after loading completes
  React.useEffect(() => {
    if (!loading && matchingRows.size > 0 && firstMatchRef.current?.scrollIntoView) {
      firstMatchRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [loading, matchingRows]);

  if (loading) {
    return (
      <div
        aria-label={t("Loading Excel file...")}
        aria-live="polite"
        className="flex items-center justify-center h-[60vh]"
        role="status"
      >
        <Spinner label={t("Loading Excel file...")} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[60vh]" role="alert">
        <p className="text-danger">{error}</p>
      </div>
    );
  }

  if (sheets.length === 0) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-default-500">{t("No data found in Excel file")}</p>
      </div>
    );
  }

  return (
    <SheetView
      activeSheet={activeSheet}
      currentSheet={currentSheet}
      firstMatchRef={firstMatchRef}
      matchingRows={matchingRows}
      sheets={sheets}
      onSelectSheet={setActiveSheet}
    />
  );
};

export default ExcelViewer;
